import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useToast } from '@/hooks/use-toast';
import { jobKeys } from '@/hooks/useJobs';
import type { JobCardFields } from '@jobos/shared';

const round2 = (n: number) => Math.round(n * 100) / 100;

// Per-row cost for the spare template rows a job may fill in. `?? []` because a
// job cached before custom rows existed has no `custom` key at all.
const customCosts = (lines: JobCardFields['reimbursement']['custom'] | undefined) =>
  (lines ?? []).map((l) => round2(l.rate * l.qty));

const sum = (values: number[]) => values.reduce((s, n) => s + n, 0);

export function computeJobCardTotals(f: JobCardFields) {
  const labor = {
    management: round2(f.labor.management.rate * f.labor.management.hours),
    office: round2(f.labor.office.rate * f.labor.office.hours),
    assistant: round2(f.labor.assistant.rate * f.labor.assistant.hours),
  };
  const reimbursement = {
    toll: round2(f.reimbursement.toll.rate * f.reimbursement.toll.qty),
    mileage: round2(f.reimbursement.mileage.rate * f.reimbursement.mileage.qty),
  };
  const reimbursementCustom = customCosts(f.reimbursement.custom);
  const directSum = Object.values(f.directCosts).reduce((s, n) => s + n, 0);
  return {
    labor,
    reimbursement,
    reimbursementCustom,
    laborTotal: round2(labor.management + labor.office + labor.assistant),
    reimbursementTotal: round2(
      reimbursement.toll +
        reimbursement.mileage +
        directSum +
        sum(reimbursementCustom),
    ),
  };
}

export function useSaveJobCardFields(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (fields: JobCardFields) => {
      const res = await apiClient.put<{ jobCardFields: JobCardFields }>(
        `/jobs/${jobId}/job-card-fields`,
        fields,
      );
      return res.jobCardFields;
    },
    onSuccess: (saved) => {
      // Patch the cached job in place rather than invalidating + refetching.
      // A refetch would re-seed the card's local state and could clobber a
      // value the user is editing in the sub-second window after a blur-save.
      queryClient.setQueryData(
        jobKeys.detail(jobId),
        (old: unknown) =>
          old && typeof old === 'object'
            ? { ...(old as Record<string, unknown>), jobCardFields: saved }
            : old,
      );
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save job card fields',
        variant: 'destructive',
      });
    },
  });
}
